import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PageHeader, Panel, DataTable, InlineFilters, QuickFilters, Tag, Button, IconButton, Dialog, ConfirmDialog, Field, TextInput, TextArea,
  Select, Switch, ComboBox, Avatar, EmptyState, Spinner, Icon, Banner, FileDrop, Sheet, MenuButton, useCkToast, ckConfirm, exportRows,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import MemberCardNext from '../members/MemberCardNext.jsx';
import { downloadInboxMessage, downloadInboxZip, formatBytes } from '../../next/inboxDownload.js';
import { IS_MISHTAMERET, LESSON_CHANNELS, contentLessonChannels } from '../../data/lessonChannels.js';
import { TRACTATE_NAMES, getMaxDaf, formatLessonPageRefDisplay } from '../../data/gemaraTractates.js';
import { formatHebrewDate, formatHebrewDateTime } from '../../utils/hebrewDate';
import {
  Tabs, FormGrid, Row, AudioPlayer, apiError, useServerTable, sortParams,
} from '../../next/ui';

/**
 * תוכן — new design. Same endpoints as Content:
 *   /messages (general + IVR inbox), /lesson-recorders, /drashas (+ channel settings, profile image, audio, reorder),
 *   /shinun-magidim.
 */
const yemotProxyPath = (p) => String(p || '').replace(/^ivr2:/i, '').replace(/^\//, '').trim();
const sourceLabel = (s) => (s === 'phone_ivr' ? 'טלפון' : s === 'import' ? 'ייבוא' : 'העלאה');
/** התאריך היום בישראל — YYYY-MM-DD */
const todayKey = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(new Date());
/** ערוצים שיש להם מסלול בלוח הלימודים (העלאה מרוכזת, שיוך הקלטות לדף) */
const channelHasTrack = (ch) => /^(daf_yomi_|shinun_)/.test(String(ch || ''));
const PHONE_PAGE_ASSIGN_OPTIONS = [
  { value: '', label: 'בלי שיוך לדף' },
  { value: 'today', label: 'הדף של היום' },
  { value: 'next', label: 'הדף הבא (מקליטים בערב למחר)' },
];
const MSG_STATUS = { draft: { label: 'טיוטה', tone: 'neutral' }, active: { label: 'פעילה', tone: 'primary' }, paused: { label: 'מושהית', tone: 'warning' } };

/* ── הודעות ─────────────────────────────────────────────────────── */
function MessagesTab() {
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const t = useServerTable(async (q) => {
    const { data } = await api.get('/messages', {
      params: { page: q.page, pageSize: q.pageSize, ...sortParams(q), status, type, excludeInbox: 1 },
    });
    return { rows: data.data || [], total: data.total };
  }, [status, type]);
  const onExport = async () => {
    const { data } = await api.get('/messages', { params: { all: 1, ...sortParams(t.query), status, type, excludeInbox: 1 } });
    return data.data || [];
  };
  return (
    <>
      <InlineFilters
        items={[
          { key: 'status', label: 'סטטוס', type: 'chips', value: status, onChange: (v) => { setStatus(v); t.resetPage(); }, options: [{ value: '', label: 'הכל' }, { value: 'active', label: 'פעילה' }, { value: 'paused', label: 'מושהית' }, { value: 'draft', label: 'טיוטה' }] },
          { key: 'type', label: 'סוג', type: 'chips', value: type, onChange: (v) => { setType(v); t.resetPage(); }, options: [{ value: '', label: 'הכל' }, { value: 'general', label: 'כללית' }, { value: 'personal', label: 'אישית' }] },
        ]}
      />
      <DataTable
        mode="server"
        {...t.tableProps}
        rowKey="_id"
        selectable={false}
        searchable={false}
        storageKey="bchinot-messages"
        exportFilename="הודעות"
        onExport={async ({ format, columns }) => {
          await exportRows(await onExport(), columns, { format, filename: 'הודעות' });
        }}
        emptyTitle="אין הודעות"
        columns={[
          { key: 'title', label: 'כותרת', render: (r) => <b>{r.title}</b> },
          { key: 'type', label: 'סוג', getValue: (r) => (r.type === 'general' ? 'כללית' : 'אישית') },
          { key: 'priority', label: 'עדיפות', type: 'number' },
          { key: 'status', label: 'סטטוס', getValue: (r) => MSG_STATUS[r.status]?.label || r.status, render: (r) => (MSG_STATUS[r.status] ? <Tag tone={MSG_STATUS[r.status].tone}>{MSG_STATUS[r.status].label}</Tag> : r.status) },
          { key: 'validFrom', label: 'מ-', type: 'date', render: (r) => formatHebrewDate(r.validFrom) || '—' },
          { key: 'validTo', label: 'עד', type: 'date', render: (r) => formatHebrewDate(r.validTo) || '—' },
        ]}
      />
    </>
  );
}

/* ── הודעות למערכת (תיבת IVR) ───────────────────────────────────── */
const INBOX_STATUS = {
  new: { label: 'חדש', tone: 'primary', icon: 'sparkle' },
  played: { label: 'הושמע', tone: 'neutral', icon: 'play' },
  todo: { label: 'לטיפול', tone: 'warning', icon: 'flag' },
  done: { label: 'הטיפול הסתיים', tone: 'success', icon: 'check' },
};
const INBOX_STATUS_OPTIONS = Object.entries(INBOX_STATUS).map(([value, s]) => ({ value, label: s.label }));
const inboxAudioPath = (r) => {
  const body = String(r.body || '');
  const fromSource = body.startsWith('yemot-source:') ? body.slice('yemot-source:'.length) : '';
  return yemotProxyPath(fromSource || r.audioUrl);
};

const memberNameOf = (r) => `${r.member?.firstName || ''} ${r.member?.lastName || ''}`.trim();

function SystemMessagesTab() {
  const toast = useCkToast();
  const { hasPermission } = useAuth();
  const canOpenMember = hasPermission('members', 'read');
  const [statusFilter, setStatusFilter] = useState('');
  const [counts, setCounts] = useState(null);
  const [notDownloaded, setNotDownloaded] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [downloading, setDownloading] = useState(null); // { onlyNew, bytes } while a ZIP is being prepared
  const t = useServerTable(async (q) => {
    const { data } = await api.get('/messages', { params: { page: q.page, pageSize: q.pageSize, ...sortParams(q), inbox: 'ivr', inboxStatus: statusFilter } });
    if (data.inboxCounts) setCounts(data.inboxCounts);
    if (data.notDownloadedCount != null) setNotDownloaded(data.notDownloadedCount);
    return { rows: data.data || [], total: data.total };
  }, [statusFilter]);
  const phoneOf = (r) => { const raw = String(r.createdBy || ''); return raw.startsWith('ivr-') ? raw.slice(4) : raw; };

  const [downloadingId, setDownloadingId] = useState(null);

  const download = async (onlyNew) => {
    if (downloading) return;
    setDownloading({ onlyNew, bytes: 0 });
    try {
      const { fileCount } = await downloadInboxZip({ onlyNew, onProgress: (bytes) => setDownloading((d) => (d ? { ...d, bytes } : d)) });
      toast.success(fileCount ? `הורדו ${fileCount} הקלטות` : 'הקובץ הורד');
      t.reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const downloadOne = async (id) => {
    if (downloadingId) return;
    setDownloadingId(id);
    try {
      await downloadInboxMessage(id);
      t.reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const patchRow = (id, inboxStatus) => t.setRows((rows) => rows.map((r) => (r._id === id ? { ...r, inboxStatus } : r)));
  const bump = (from, to) => setCounts((c) => (c ? { ...c, [from]: Math.max(0, (c[from] || 0) - 1), [to]: (c[to] || 0) + 1 } : c));
  const setStatus = async (r, next) => {
    const prev = r.inboxStatus || 'new';
    if (next === prev) return;
    patchRow(r._id, next);
    bump(prev, next);
    try {
      await api.patch(`/messages/${r._id}/inbox-status`, { status: next });
      toast.success(`סומן «${INBOX_STATUS[next].label}»`);
    } catch (e) {
      patchRow(r._id, prev);
      bump(next, prev);
      toast.error(apiError(e, 'שגיאה בעדכון הסטטוס'));
    }
  };
  // השמעה ראשונה מסמנת "הושמע" בשרת — מעדכנים גם את השורה
  const onPlayed = (r) => { if ((r.inboxStatus || 'new') === 'new') { patchRow(r._id, 'played'); bump('new', 'played'); } };

  const total = counts ? Object.values(counts).reduce((s, n) => s + n, 0) : undefined;
  return (
    <>
      <QuickFilters
        value={statusFilter || 'all'}
        onChange={(k) => { setStatusFilter(k === 'all' || k === statusFilter ? '' : k); t.resetPage(); }}
        loading={t.loading && !counts}
        items={[
          { key: 'all', label: 'הכול', value: total, icon: 'inbox', tone: 'neutral' },
          { key: 'new', label: 'חדש', value: counts?.new, icon: 'sparkle', tone: 'primary', warn: (counts?.new || 0) > 0 },
          { key: 'todo', label: 'לטיפול', value: counts?.todo, icon: 'flag', tone: 'warning' },
          { key: 'played', label: 'הושמע', value: counts?.played, icon: 'play', tone: 'neutral' },
          { key: 'done', label: 'הטיפול הסתיים', value: counts?.done, icon: 'check', tone: 'success' },
        ]}
      />
      <DataTable
        mode="server"
        {...t.tableProps}
        rowKey="_id"
        selectable={false}
        searchable={false}
        storageKey="bchinot-system-messages"
        exportFilename="הודעות למערכת"
        onExport={async ({ format, columns }) => {
          const { data } = await api.get('/messages', { params: { all: 1, ...sortParams(t.query), inbox: 'ivr', inboxStatus: statusFilter } });
          await exportRows(data.data || [], columns, { format, filename: 'הודעות למערכת' });
        }}
        emptyTitle={statusFilter ? `אין הודעות בסטטוס «${INBOX_STATUS[statusFilter].label}»` : 'אין הודעות למערכת'}
        toolbarEnd={(
          <MenuButton
            align="end"
            width={260}
            items={[
              {
                key: 'new',
                icon: 'download',
                label: 'הורדת מה שטרם הורד',
                hint: notDownloaded ? `${notDownloaded} הקלטות` : 'הכול כבר הורד',
                disabled: !notDownloaded,
                onClick: () => download(true),
              },
              { key: 'all', icon: 'download', label: 'הורדת כל ההקלטות', hint: total ? `${total} הקלטות` : undefined, disabled: !total, onClick: () => download(false) },
            ]}
            renderButton={(p) => (
              <Button {...p} size="sm" icon="download" caret disabled={!!downloading}>
                {downloading ? `מכין ZIP…${downloading.bytes ? ` ${formatBytes(downloading.bytes)}` : ''}` : `הורדת הקלטות${notDownloaded ? ` (${notDownloaded} חדשות)` : ''}`}
              </Button>
            )}
          />
        )}
        rowMenu={(r) => [
          ...(r.member && canOpenMember ? [{ key: 'member', label: 'כרטיס חבר', icon: 'user', onClick: () => setMemberId(String(r.member._id)) }, { divider: true }] : []),
          ...INBOX_STATUS_OPTIONS.filter((o) => o.value !== (r.inboxStatus || 'new')).map((o) => ({
            key: o.value, label: `סמן «${o.label}»`, icon: INBOX_STATUS[o.value].icon, onClick: () => setStatus(r, o.value),
          })),
        ]}
        columns={[
          {
            key: 'inboxStatus',
            label: 'סטטוס',
            type: 'enum',
            sortable: false,
            filterable: false,
            getValue: (r) => INBOX_STATUS[r.inboxStatus || 'new'].label,
            render: (r) => { const s = INBOX_STATUS[r.inboxStatus || 'new']; return <Tag tone={s.tone} icon={s.icon}>{s.label}</Tag>; },
            editable: { options: INBOX_STATUS_OPTIONS, onSave: (r, v) => setStatus(r, v) },
          },
          { key: 'createdAt', label: 'תאריך', type: 'date', render: (r) => <span style={{ fontWeight: (r.inboxStatus || 'new') === 'new' ? 700 : 400 }}>{r.createdAt ? formatHebrewDateTime(r.createdAt) : '—'}</span> },
          {
            key: 'member',
            label: 'חבר',
            sortable: false,
            filterable: false,
            getValue: (r) => memberNameOf(r) || 'לא מזוהה',
            render: (r) => {
              const name = memberNameOf(r);
              if (!r.member) return <span className="ck-muted">לא מזוהה</span>;
              const hint = r.member.matchedBy === 'phoneAlt' ? 'זוהה לפי הטלפון הנוסף' : undefined;
              return canOpenMember
                ? <button type="button" className="nx-link" title={hint ? `${hint} · פתיחת כרטיס החבר` : 'פתיחת כרטיס החבר'} onClick={(e) => { e.stopPropagation(); setMemberId(String(r.member._id)); }}><b>{name || 'ללא שם'}</b></button>
                : <b title={hint}>{name || 'ללא שם'}</b>;
            },
          },
          { key: 'createdBy', label: 'טלפון מתקשר', ltr: true, copy: true, getValue: (r) => phoneOf(r) || '—' },
          { key: 'title', label: 'סוג', getValue: (r) => (String(r.title || '').includes('הנהלה') ? 'להנהלה' : 'למערכת'), render: (r) => (String(r.title || '').includes('הנהלה') ? <Tag tone="violet">להנהלה</Tag> : <Tag tone="info">למערכת</Tag>) },
          {
            key: 'audio',
            label: 'השמעה',
            sortable: false,
            filterable: false,
            exportable: false,
            width: 300,
            render: (r) => (inboxAudioPath(r) && r._id
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={(e) => e.stopPropagation()} role="presentation"><AudioPlayer src={`/messages/${r._id}/audio`} label="הודעה למערכת" onFirstPlay={() => onPlayed(r)} /><IconButton size="sm" icon="download" label="הורדת ההקלטה" disabled={downloadingId === r._id} onClick={() => downloadOne(r._id)} /></span>
              : <span className="ck-muted">אין הקלטה</span>),
          },
          { key: 'downloadedAt', label: 'הורד', type: 'date', render: (r) => (r.downloadedAt ? formatHebrewDateTime(r.downloadedAt) : <span className="ck-muted">טרם הורד</span>) },
        ]}
      />

      <Sheet open={!!memberId} onClose={() => setMemberId(null)} width={1080} ariaLabel="כרטיס חבר">
        {memberId ? <div className="nx-sheet-body"><MemberCardNext key={memberId} recordId={memberId} onClose={() => setMemberId(null)} /></div> : null}
      </Sheet>
    </>
  );
}

/* ── שיעורים / דרשות של ערוץ ───────────────────────────────────── */
const emptyDrashaForm = () => ({
  title: '', speaker: '', date: new Date().toISOString().slice(0, 10), category: '', status: 'inactive', announceDetails: true,
  tractate: '', daf: '', amud: '', announcePageRef: false,
});

/** lesson / drasha form fields — shared by the per-channel dialog and the unified "add content" dialog */
function DrashaFormFields({ form, setForm, editing }) {
  const hasPageRef = !!(form.tractate || form.daf || form.amud);
  const maxDaf = getMaxDaf(form.tractate);
  return (
    <>
      <FormGrid cols={2}>
        <Field label="כותרת (אופציונלי)"><TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-autofocus /></Field>
        <Field label="מרצה (אופציונלי)"><TextInput value={form.speaker} onChange={(e) => setForm((f) => ({ ...f, speaker: e.target.value }))} /></Field>
        <Field label="תאריך *"><TextInput type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></Field>
        <Field label="קטגוריה (אופציונלי)"><TextInput value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></Field>
      </FormGrid>
      {IS_MISHTAMERET ? (
        <div className="nx-subcard">
          <Row justify="space-between">
            <span className="nx-sub-h" style={{ margin: 0 }}>שיוך לדף / עמוד <small className="ck-muted">(אופציונלי)</small></span>
            <Button size="sm" variant="ghost" disabled={!hasPageRef} onClick={() => setForm((f) => ({ ...f, tractate: '', daf: '', amud: '', announcePageRef: false }))}>נקה שיוך</Button>
          </Row>
          <FormGrid cols={3}>
            <Field label="מסכת">
              <ComboBox
                value={form.tractate ? { value: form.tractate, label: form.tractate } : null}
                onChange={(o) => setForm((f) => ({ ...f, tractate: o?.value || '' }))}
                options={TRACTATE_NAMES.map((n) => ({ value: n, label: n }))}
                placeholder="בחרו מסכת…"
              />
            </Field>
            <Field label="דף" hint={form.tractate && maxDaf ? `מקסימום: ${maxDaf} (${toHebrewNumeral(maxDaf)})` : 'מינימום דף 2'}>
              <TextInput type="number" min={2} max={maxDaf || undefined} value={form.daf} onChange={(e) => setForm((f) => ({ ...f, daf: e.target.value }))} />
            </Field>
            <Field label="עמוד">
              <Select value={form.amud} onChange={(v) => setForm((f) => ({ ...f, amud: v }))} options={[{ value: '', label: 'כל הדף' }, { value: 'a', label: 'א' }, { value: 'b', label: 'ב' }]} />
            </Field>
          </FormGrid>
          <Switch label="השמע שיוך דף בטלפון" checked={form.announcePageRef === true} disabled={!hasPageRef} onChange={(v) => setForm((f) => ({ ...f, announcePageRef: v }))} />
        </div>
      ) : null}
      <Switch label="השמע כותרת ומרצה בטלפון" hint="כבו את המתג אם הפרטים כבר מושמעים בהקלטה עצמה" checked={form.announceDetails !== false} onChange={(v) => setForm((f) => ({ ...f, announceDetails: v }))} />
      {editing ? <Switch label="פעילה" hint="רק אחרי סנכרון אודיו לימות" checked={form.status === 'active'} onChange={(v) => setForm((f) => ({ ...f, status: v ? 'active' : 'inactive' }))} /> : null}
    </>
  );
}

/** validates the form and builds the /drashas payload (same rules as Content) — returns { error } or { payload } */
function buildDrashaPayload(form, channel) {
  if (!form.date) return { error: 'תאריך חובה' };
  const tractate = String(form.tractate || '').trim();
  const dafRaw = String(form.daf || '').trim();
  const amud = form.amud === 'a' || form.amud === 'b' ? form.amud : '';
  const anyPage = !!(tractate || dafRaw || amud);
  if (anyPage && (!tractate || !dafRaw)) return { error: 'שיוך דף דורש מסכת ומספר דף' };
  if (anyPage) {
    const dafNum = Number(dafRaw);
    const max = getMaxDaf(tractate);
    if (!Number.isInteger(dafNum) || dafNum < 2 || (max != null && dafNum > max)) return { error: max != null ? `מספר דף חייב להיות בין 2 ל-${max}` : 'מספר דף לא תקין' };
  }
  return {
    payload: {
      title: String(form.title || '').trim(), speaker: String(form.speaker || '').trim(), date: form.date, category: String(form.category || '').trim(),
      channel, status: form.status, announceDetails: form.announceDetails !== false,
      tractate: anyPage ? tractate : '', daf: anyPage ? Number(dafRaw) : null, amud: anyPage ? amud : '', announcePageRef: anyPage ? form.announcePageRef === true : false,
    },
  };
}

function DrashasTab({
  channel = 'drasha', title = 'דרשות / שמועס׳ן', subtitle = 'מופיעות בשלוחה 5 בטלפון. חזרה למקום נשמרת בימות אחרי העלאת אודיו.', itemLabel = 'דרשה',
  ivrPath = '', refreshKey = 0, onAdd,
}) {
  const toast = useCkToast();
  const [drashas, setDrashas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyDrashaForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [channelSettings, setChannelSettings] = useState({ announceDetails: true, announcePageRef: false, phonePageAssign: null, hasProfileImage: false });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [showScheduled, setShowScheduled] = useState(false);
  const [savingChannelSettings, setSavingChannelSettings] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState('');
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const fileInputRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const uploadTargetIdRef = useRef(null);
  const previewRef = useRef('');

  const revokePreview = useCallback(() => {
    if (previewRef.current) { URL.revokeObjectURL(previewRef.current); previewRef.current = ''; }
    setProfilePreviewUrl('');
  }, []);
  const loadProfilePreview = useCallback(async () => {
    revokePreview();
    try {
      const { data } = await api.get(`/drashas/channel-settings/${encodeURIComponent(channel)}/profile-image`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      previewRef.current = url;
      setProfilePreviewUrl(url);
    } catch { revokePreview(); }
  }, [channel, revokePreview]);
  const loadChannelSettings = useCallback(async () => {
    try {
      const { data } = await api.get(`/drashas/channel-settings/${encodeURIComponent(channel)}`);
      const hasProfileImage = data.hasProfileImage === true;
      setChannelSettings({ announceDetails: data.announceDetails !== false, announcePageRef: data.announcePageRef === true, phonePageAssign: data.phonePageAssign ?? null, hasProfileImage });
      if (hasProfileImage) await loadProfilePreview(); else revokePreview();
    } catch { /* keep defaults */ }
  }, [channel, loadProfilePreview, revokePreview]);
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/drashas', { params: { page: 1, pageSize: 2000, channel } });
      setDrashas(data.data || []);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בטעינת תוכן'));
    } finally {
      setLoading(false);
    }
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [loadData, refreshKey]);
  useEffect(() => { loadChannelSettings(); }, [loadChannelSettings]);
  useEffect(() => () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); }, []);

  const saveChannelSettings = async (patch) => {
    const next = { ...channelSettings, ...patch };
    setChannelSettings(next);
    setSavingChannelSettings(true);
    try {
      await api.put(`/drashas/channel-settings/${encodeURIComponent(channel)}`, {
        announceDetails: next.announceDetails,
        announcePageRef: next.announcePageRef,
        ...(next.phonePageAssign != null ? { phonePageAssign: next.phonePageAssign } : {}),
      });
      toast.success('phonePageAssign' in patch ? 'הגדרת שיוך הקלטות הטלפון נשמרה' : 'הגדרות הערוץ עודכנו וחלו על כל השיעורים');
      await loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירת הגדרות הערוץ'));
      await loadChannelSettings();
    } finally {
      setSavingChannelSettings(false);
    }
  };

  const onProfileImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    setUploadingProfile(true);
    try {
      const { data } = await api.post(`/drashas/channel-settings/${encodeURIComponent(channel)}/profile-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setChannelSettings((p) => ({ ...p, hasProfileImage: data.hasProfileImage !== false }));
      await loadProfilePreview();
      toast.success('תמונת הפרופיל נשמרה לערוץ');
    } catch (err) {
      toast.error(apiError(err, 'שגיאה בהעלאת תמונת פרופיל'));
    } finally {
      setUploadingProfile(false);
    }
  };
  const removeProfileImage = async () => {
    setUploadingProfile(true);
    try {
      await api.delete(`/drashas/channel-settings/${encodeURIComponent(channel)}/profile-image`);
      setChannelSettings((p) => ({ ...p, hasProfileImage: false }));
      revokePreview();
      toast.success('תמונת הפרופיל הוסרה');
    } catch (err) {
      toast.error(apiError(err, 'שגיאה בהסרת תמונת פרופיל'));
    } finally {
      setUploadingProfile(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyDrashaForm(), announceDetails: channelSettings.announceDetails !== false, announcePageRef: channelSettings.announcePageRef === true });
    setDialogOpen(true);
  };
  const openEdit = (d) => {
    setEditId(d._id);
    setForm({
      title: d.title || '', speaker: d.speaker || '', date: d.date ? new Date(d.date).toISOString().slice(0, 10) : '', category: d.category || '',
      status: d.status === 'active' ? 'active' : 'inactive', announceDetails: d.announceDetails !== false, tractate: d.tractate || '',
      daf: d.daf != null && d.daf !== '' ? String(d.daf) : '', amud: d.amud === 'a' || d.amud === 'b' ? d.amud : '', announcePageRef: d.announcePageRef === true,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const { error, payload } = buildDrashaPayload(form, channel);
    if (error) { toast.error(error); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/drashas/${editId}`, payload);
        toast.success(`${itemLabel} עודכן/ה`);
        setDialogOpen(false);
        loadData();
      } else {
        const { data: created } = await api.post('/drashas', { ...payload, status: 'inactive' });
        toast.success(`${itemLabel} נוצר/ה — העלו קובץ שמע ואז הפעילו`);
        setDialogOpen(false);
        await loadData();
        uploadTargetIdRef.current = created._id;
        fileInputRef.current?.click();
      }
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/drashas/${deleteTarget._id}`);
      toast.success(`${itemLabel} נמחק/ה`);
      setDeleteTarget(null);
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
      setDeleteTarget(null);
    }
  };

  const triggerUpload = (id) => { uploadTargetIdRef.current = id; fileInputRef.current?.click(); };
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const id = uploadTargetIdRef.current;
    e.target.value = '';
    if (!file || !id) return;
    const fd = new FormData();
    fd.append('audio', file);
    setUploadingId(id);
    try {
      const { data: res } = await api.post(`/drashas/${id}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.yemotSynced) toast.success('האודיו הועלה וסונכרן לימות');
      else toast('הקובץ נשמר, אך הסנכרון לימות נכשל — בדקו טוקן ימות', { tone: 'warning' });
      loadData();
    } catch (err) {
      toast.error(apiError(err, 'שגיאה בהעלאת אודיו'));
    } finally {
      setUploadingId(null);
      uploadTargetIdRef.current = null;
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/drashas/${d._id}`, { status: d.status === 'active' ? 'inactive' : 'active' });
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    }
  };

  // שיעורים מתוזמנים (העלאה מרוכזת) מוסתרים בטלפון עד התאריך שלהם — מוצגים בנפרד, בלי סידור
  const published = drashas.filter((d) => !d.scheduled);
  const scheduled = drashas.filter((d) => d.scheduled).sort((a, b) => new Date(a.date) - new Date(b.date));

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= published.length || reordering) return;
    const prev = drashas;
    const next = [...published];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setDrashas([...next, ...scheduled]);
    setReordering(true);
    try {
      await api.put('/drashas/reorder', { ids: next.map((d) => d._id), channel });
    } catch (e) {
      setDrashas(prev);
      toast.error(apiError(e, 'שגיאה בשינוי הסדר'));
      loadData();
    } finally {
      setReordering(false);
    }
  };

  const renderLesson = (d, index) => {
    const pageRef = IS_MISHTAMERET ? formatLessonPageRefDisplay(d, toHebrewNumeral) : '';
    const playable = yemotProxyPath(d.yemotFilePath) || (d.yemotSynced && d.audioUrl);
    const synced = d.yemotSynced && (d.yemotFilePath || d.audioUrl);
    return (
      <div key={d._id} className={`nx-lesson ${d.status === 'active' ? '' : 'is-off'}`}>
        <div className="nx-lesson__order">
          {d.scheduled ? <Icon name="clock" size={16} /> : (
            <>
              <IconButton size="sm" icon="up" label="העלאה בסדר" disabled={reordering || index === 0} onClick={() => move(index, -1)} />
              <span className="nx-num">{index + 1}</span>
              <IconButton size="sm" icon="down" label="הורדה בסדר" disabled={reordering || index === published.length - 1} onClick={() => move(index, 1)} />
            </>
          )}
        </div>
        <div className="nx-lesson__main">
          <div className="nx-lesson__title">
            <b>{d.title?.trim() ? d.title : 'ללא כותרת'}</b>
            {d.scheduled ? <Tag tone="info" icon="clock">{`ייפתח ב־${formatHebrewDate(d.date)}`}</Tag> : null}
            {d.source && d.source !== 'admin_upload' ? <Tag tone={d.source === 'phone_ivr' ? 'info' : 'neutral'} icon={d.source === 'phone_ivr' ? 'phone' : 'upload'}>{sourceLabel(d.source)}</Tag> : null}
            {d.category ? <Tag>{d.category}</Tag> : null}
          </div>
          <div className="nx-lesson__meta">
            {d.speaker?.trim() ? <span><Icon name="user" size={13} />{d.speaker}</span> : null}
            {d.date ? <span><Icon name="calendar" size={13} />{formatHebrewDate(d.date)}</span> : null}
            {pageRef ? <span><Icon name="book" size={13} />{`${pageRef}${d.announcePageRef === true ? ' · מושמע' : ''}`}</span> : null}
            {d.announceDetails === false ? <span>ללא השמעת פרטים</span> : null}
          </div>
          <div className="nx-lesson__file">
            {synced
              ? <span title={d.audioFilename || ''}><Icon name="check" size={13} />ימות: <span className="nx-code">{d.yemotFilePath || d.audioUrl}</span></span>
              : <span className="is-warn"><Icon name="alert" size={13} />אין אודיו מסונכרן לימות</span>}
          </div>
        </div>
        <div className="nx-lesson__player">
          {playable ? <AudioPlayer src={`/drashas/${d._id}/audio`} label={d.title || 'שיעור'} compact /> : (
            <Button size="sm" icon="upload" disabled={uploadingId === d._id} onClick={() => triggerUpload(d._id)}>{uploadingId === d._id ? 'מעלה…' : 'העלאת אודיו'}</Button>
          )}
        </div>
        <div className="nx-lesson__acts">
          <Switch checked={d.status === 'active'} onChange={() => toggleActive(d)} label={d.status === 'active' ? 'פעילה' : 'כבויה'} />
          {playable ? (uploadingId === d._id ? <Spinner size={18} /> : <IconButton icon="upload" label="החלפת אודיו" onClick={() => triggerUpload(d._id)} />) : null}
          <IconButton icon="edit" label="עריכה" onClick={() => openEdit(d)} />
          <IconButton icon="trash" label="מחיקה" onClick={() => setDeleteTarget(d)} />
        </div>
      </div>
    );
  };

  const activeCount = published.filter((d) => d.status === 'active').length;
  const hasTrack = IS_MISHTAMERET && channelHasTrack(channel);
  const addNew = onAdd || openCreate;

  return (
    <div className="nx-stack">
      <input ref={fileInputRef} type="file" accept="audio/*" hidden onChange={onFileSelected} />
      <input ref={profileImageInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" hidden onChange={onProfileImage} />

      <div className="nx-channel-head">
        {IS_MISHTAMERET ? (
          <button type="button" className="nx-channel-head__avatar" title={channelSettings.hasProfileImage ? 'החלפת תמונת פרופיל לערוץ' : 'העלאת תמונת פרופיל לערוץ (מוצגת בפורטל)'} disabled={uploadingProfile} onClick={() => profileImageInputRef.current?.click()}>
            <Avatar src={profilePreviewUrl || undefined} text={title} size={56} />
            <span className="nx-channel-head__cam"><Icon name={uploadingProfile ? 'refresh' : 'image'} size={13} /></span>
          </button>
        ) : <span className="nx-channel-head__icon"><Icon name="play" size={22} /></span>}
        <div className="nx-channel-head__main">
          <h2>{title}</h2>
          <div className="nx-channel-head__meta">
            {ivrPath ? <Tag tone="primary" icon="phone">{`שלוחה ${ivrPath}`}</Tag> : null}
            <Tag tone="success">{`${activeCount} פעילים`}</Tag>
            <Tag>{`${published.length} סה״כ`}</Tag>
            {scheduled.length ? <Tag tone="info" icon="clock">{`${scheduled.length} מתוזמנים`}</Tag> : null}
            {channelSettings.hasProfileImage ? <button type="button" className="nx-link" disabled={uploadingProfile} onClick={removeProfileImage}><small>הסרת תמונה</small></button> : null}
          </div>
          <small className="ck-muted">{`${subtitle} הסדר כאן הוא סדר התפריט בטלפון.`}</small>
        </div>
        <div className="nx-channel-head__side">
          <Row gap={6}>
            {hasTrack ? <Button icon="upload" onClick={() => setBulkOpen(true)}>העלאה מרוכזת</Button> : null}
            <Button variant="primary" icon="plus" onClick={addNew}>{`${itemLabel} חדש/ה`}</Button>
          </Row>
          <div className="nx-channel-head__switches" title="חל מיד על כל השיעורים בערוץ, ומשמש כברירת מחדל לשיעורים חדשים">
            <Switch label="השמע כותרת ומרצה" checked={channelSettings.announceDetails !== false} disabled={savingChannelSettings} onChange={(v) => saveChannelSettings({ announceDetails: v })} />
            {IS_MISHTAMERET ? <Switch label="השמע שיוך דף" checked={channelSettings.announcePageRef === true} disabled={savingChannelSettings} onChange={(v) => saveChannelSettings({ announcePageRef: v })} /> : null}
            {hasTrack && channelSettings.phonePageAssign != null ? (
              <label className="nx-inline-select" title="הקלטה חדשה בטלפון משויכת אוטומטית לדף לפי לוח הלימודים — כך היא מושמעת בפתיח כשיעור היום">
                <small>הקלטה בטלפון:</small>
                <Select value={channelSettings.phonePageAssign} disabled={savingChannelSettings} onChange={(v) => saveChannelSettings({ phonePageAssign: v })} options={PHONE_PAGE_ASSIGN_OPTIONS} />
              </label>
            ) : null}
          </div>
        </div>
      </div>

      {loading ? <div className="ck-empty"><Spinner size={24} /></div> : drashas.length ? (
        <>
          {published.length ? (
            <div className="nx-lessons">
              {published.map((d, index) => renderLesson(d, index))}
            </div>
          ) : null}
          {scheduled.length ? (
            <div className="nx-stack">
              <Row justify="space-between">
                <span className="nx-sub-h" style={{ margin: 0 }}>{`שיעורים מתוזמנים (${scheduled.length}) — מוסתרים בטלפון עד היום שלהם בלוח`}</span>
                <Button size="sm" variant="ghost" icon={showScheduled ? 'up' : 'down'} onClick={() => setShowScheduled((v) => !v)}>{showScheduled ? 'הסתרה' : 'הצגה'}</Button>
              </Row>
              {showScheduled ? <div className="nx-lessons">{scheduled.map((d) => renderLesson(d, -1))}</div> : null}
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState icon="play" title="אין עדיין תוכן בשלוחה הזו" action={<Button variant="primary" icon="plus" onClick={addNew}>{`${itemLabel} חדש/ה`}</Button>}>
          הוסיפו פריט והעלו קובץ שמע — הוא יופיע בתפריט הטלפון לפי הסדר כאן.
        </EmptyState>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? `עריכת ${itemLabel}` : `${itemLabel} חדש/ה`}
        icon={editId ? 'edit' : 'plus'}
        size="md"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={saving} onClick={handleSave}>{saving ? 'שומר…' : 'שמירה'}</Button>
          </>
        )}
      >
        <div className="nx-stack"><DrashaFormFields form={form} setForm={setForm} editing={!!editId} /></div>
      </Dialog>

      {hasTrack ? (
        <BulkUploadDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          channel={channel}
          channelLabel={title}
          defaultSpeaker={drashas.find((d) => d.speaker?.trim())?.speaker || ''}
          onDone={loadData}
        />
      ) : null}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`מחיקת ${itemLabel}`}
        message={<p className="ck-confirm__msg">{`למחוק את «${deleteTarget?.title?.trim() || 'ללא כותרת'}»? הקובץ יימחק גם מימות אם קיים.`}</p>}
        confirmLabel="מחיקה"
      />
    </div>
  );
}

/* ── מג"ש בחצר קדשינו ──────────────────────────────────────────── */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
function ShinunMagashTab({ refreshKey = 0, onAdd }) {
  const toast = useCkToast();
  const [magidim, setMagidim] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', menuKey: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/shinun-magidim', { params: { all: '1' } });
      const rows = data.data || [];
      setMagidim(rows);
      setSelectedChannel((prev) => {
        if (prev && rows.some((m) => m.channel === prev && m.status === 'active')) return prev;
        return rows.find((m) => m.status === 'active')?.channel || '';
      });
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בטעינת מגידים'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const usedKeys = new Set(magidim.filter((m) => m.status === 'active' && m.id !== editId).map((m) => m.menuKey));
  const availableKeys = KEYS.filter((k) => !usedKeys.has(k) || form.menuKey === k);
  const openCreate = () => { setEditId(null); setForm({ name: '', menuKey: KEYS.find((k) => !usedKeys.has(k)) || '1', status: 'active' }); setDialogOpen(true); };
  const openEdit = (m) => { setEditId(m.id); setForm({ name: m.name || '', menuKey: m.menuKey || '', status: m.status || 'active' }); setDialogOpen(true); };
  const save = async () => {
    const name = String(form.name || '').trim();
    if (!name) { toast.error('שם מגיד נדרש'); return; }
    if (!/^[1-9]$/.test(String(form.menuKey))) { toast.error('מקש חייב להיות 1–9'); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/shinun-magidim/${editId}`, { name, menuKey: form.menuKey, status: form.status });
      else await api.post('/shinun-magidim', { name, menuKey: form.menuKey, status: form.status });
      setDialogOpen(false);
      toast.success('נשמר');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
    } finally {
      setSaving(false);
    }
  };
  const deactivate = async (m) => {
    if (!(await ckConfirm({ title: 'השבתת מגיד', message: `להשבית את המגיד «${m.name}»? השיעורים יישארו במערכת.`, confirmLabel: 'השבתה' }))) return;
    try {
      await api.delete(`/shinun-magidim/${m.id}`);
      toast.success('המגיד הושבת');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    }
  };
  const selected = magidim.filter((m) => m.status === 'active').find((m) => m.channel === selectedChannel);

  return (
    <div className="nx-stack">
      <Panel
        title="מג״ש בחצר קדשינו"
        sub="פתחו מגיד חדש — יופיע כתת־שלוחה בטלפון (2←1←5) ובפורטל. לכל מגיד העלו שיעורים בנפרד."
        icon="users"
        actions={<Button size="sm" variant="primary" icon="plus" onClick={openCreate}>מגיד חדש</Button>}
      >
        {loading ? <div className="ck-empty"><Spinner size={24} /></div> : magidim.length ? (
          <div className="nx-list">
            {magidim.map((m) => (
              <div key={m.id} className={`nx-item ${m.channel === selectedChannel ? 'is-on' : ''} ${m.status === 'active' ? '' : 'is-off'}`}>
                <span className="nx-num">{m.menuKey}</span>
                <div className="nx-item__main"><b>{m.name}</b><small>{`מקש ${m.menuKey}`}</small></div>
                <div className="nx-item__acts">
                  <Tag tone={m.status === 'active' ? 'success' : 'neutral'}>{m.status === 'active' ? 'פעיל' : 'מושבת'}</Tag>
                  {m.status === 'active' ? <Button size="sm" icon="play" onClick={() => setSelectedChannel(m.channel)}>שיעורים</Button> : null}
                  <IconButton icon="edit" label="עריכה" onClick={() => openEdit(m)} />
                  {m.status === 'active' ? <IconButton icon="trash" label="השבתה" onClick={() => deactivate(m)} /> : null}
                </div>
              </div>
            ))}
          </div>
        ) : <EmptyState compact icon="users" title="עדיין אין מגידים">לחצו «מגיד חדש».</EmptyState>}
      </Panel>

      {selected ? <DrashasTab key={selected.channel} channel={selected.channel} title={selected.name} ivrPath={`77/2/1/5 · מקש ${selected.menuKey}`} subtitle="ערוץ מג״ש." itemLabel="שיעור" refreshKey={refreshKey} onAdd={onAdd ? () => onAdd(selected.channel) : undefined} /> : null}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? 'עריכת מגיד' : 'מגיד חדש'}
        icon="users"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={saving} onClick={save}>{saving ? 'שומר…' : 'שמירה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Field label="שם המגיד"><TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-autofocus /></Field>
          <Field label="מקש בטלפון"><Select value={form.menuKey} onChange={(v) => setForm((f) => ({ ...f, menuKey: v }))} options={availableKeys} /></Field>
          {editId ? <Field label="סטטוס"><Select value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} options={[{ value: 'active', label: 'פעיל' }, { value: 'inactive', label: 'מושבת' }]} /></Field> : null}
        </div>
      </Dialog>
    </div>
  );
}

/* ── דרשנים (הקלטה בטלפון) ──────────────────────────────────────── */
function LessonRecordersTab() {
  const toast = useCkToast();
  const recorderChannels = contentLessonChannels();
  const emptyForm = () => ({ name: '', pin: '', channel: recorderChannels[0]?.id || '', notes: '', active: true });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const channelLabel = (id) => recorderChannels.find((c) => c.id === id)?.label || LESSON_CHANNELS.find((c) => c.id === id)?.label || id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/lesson-recorders');
      setRows(data.data || []);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בטעינת דרשנים'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditId(r._id || r.id);
    setForm({ name: r.name || '', pin: r.pin || '', channel: r.channel || recorderChannels[0]?.id || '', notes: r.notes || '', active: r.active !== false });
    setDialogOpen(true);
  };
  const save = async () => {
    const name = String(form.name || '').trim();
    const pin = String(form.pin || '').replace(/\D/g, '');
    if (!name) { toast.error('שם נדרש'); return; }
    if (pin.length < 4 || pin.length > 8) { toast.error('PIN חייב להיות 4–8 ספרות'); return; }
    setSaving(true);
    try {
      const payload = { name, pin, channel: form.channel, notes: form.notes, active: form.active };
      if (editId) await api.patch(`/lesson-recorders/${editId}`, payload);
      else await api.post('/lesson-recorders', payload);
      setDialogOpen(false);
      toast.success('נשמר');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
    } finally {
      setSaving(false);
    }
  };
  const deactivate = async (r) => {
    try {
      await api.delete(`/lesson-recorders/${r._id || r.id}`);
      toast.success('הושבת');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהשבתה'));
    }
  };

  return (
    <Panel
      title="דרשנים — הקלטה בטלפון"
      sub="כניסה: שלוחה 77 ← 7 ← 3 (או /77/record). אחרי אישור הקובץ נכנס לתיקיית playfile לשמיעה."
      icon="mic"
      tone="violet"
      actions={<Button size="sm" variant="primary" icon="plus" onClick={openCreate}>דרשן חדש</Button>}
    >
      {loading ? <div className="ck-empty"><Spinner size={24} /></div> : rows.length ? (
        <div className="nx-list">
          {rows.map((r) => (
            <div key={r._id || r.id} className={`nx-item ${r.active !== false ? '' : 'is-off'}`}>
              <Avatar text={r.name} size={34} />
              <div className="nx-item__main">
                <b>{r.name}</b>
                <small>PIN: <span className="nx-code">{r.pin}</span>{` · ${channelLabel(r.channel)}`}{r.listenFolder ? ` · שמיעה: ${r.listenFolder}` : ''}</small>
                {r.notes ? <small>{r.notes}</small> : null}
              </div>
              <div className="nx-item__acts">
                <Tag tone={r.active !== false ? 'success' : 'neutral'}>{r.active !== false ? 'פעיל' : 'כבוי'}</Tag>
                <IconButton icon="edit" label="עריכה" onClick={() => openEdit(r)} />
                {r.active !== false ? <IconButton icon="trash" label="השבתה" onClick={() => deactivate(r)} /> : null}
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState compact icon="mic" title="אין דרשנים">הוסיפו דרשן עם קוד PIN וערוץ שמיעה</EmptyState>}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? 'עריכת דרשן' : 'דרשן חדש'}
        icon="mic"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={saving} onClick={save}>{saving ? 'שומר…' : 'שמירה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Field label="שם *"><TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-autofocus /></Field>
          <Field label="קוד PIN *" hint="4–8 ספרות"><TextInput ltr inputMode="numeric" value={form.pin} onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 8) }))} /></Field>
          <Field label="ערוץ שמיעה"><Select value={form.channel} onChange={(v) => setForm((f) => ({ ...f, channel: v }))} options={recorderChannels.map((c) => ({ value: c.id, label: c.label }))} /></Field>
          <Field label="הערות"><TextArea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
          <Switch label="פעיל" checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} />
        </div>
      </Dialog>
    </Panel>
  );
}

/* ── העלאה מרוכזת — הרבה קבצים, כל קובץ משויך לדף לפי השם ונפתח בתאריך שלו בלוח ── */
const AUDIO_FILE_RE = /\.(mp3|wav|ogg|m4a|aac|flac|wma|webm)$/i;
const dayMonth = (dateKey) => (dateKey ? `${dateKey.slice(8, 10)}/${dateKey.slice(5, 7)}` : '');
const rowReady = (r) => r.plan && !r.plan.error && r.status !== 'done';

function BulkPageEditor({ initial, onSave, onCancel }) {
  const [ref, setRef] = useState({ tractate: initial?.tractate || '', daf: initial?.daf ? String(initial.daf) : '', amud: initial?.amud || '' });
  const maxDaf = getMaxDaf(ref.tractate);
  return (
    <div className="nx-bulk-row__edit">
      <ComboBox
        value={ref.tractate ? { value: ref.tractate, label: ref.tractate } : null}
        onChange={(o) => setRef((r) => ({ ...r, tractate: o?.value || '' }))}
        options={TRACTATE_NAMES.map((n) => ({ value: n, label: n }))}
        placeholder="מסכת…"
      />
      <TextInput type="number" min={2} max={maxDaf || undefined} placeholder="דף" value={ref.daf} onChange={(e) => setRef((r) => ({ ...r, daf: e.target.value }))} />
      <Select value={ref.amud} onChange={(v) => setRef((r) => ({ ...r, amud: v }))} options={[{ value: '', label: 'כל הדף' }, { value: 'a', label: 'א' }, { value: 'b', label: 'ב' }]} />
      <Button size="sm" variant="primary" disabled={!ref.tractate || !ref.daf} onClick={() => onSave({ ...ref, daf: Number(ref.daf) })}>בדיקה</Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>ביטול</Button>
    </div>
  );
}

function BulkUploadDialog({ open, onClose, channel, channelLabel, defaultSpeaker = '', onDone }) {
  const toast = useCkToast();
  const [rows, setRows] = useState([]);
  const [speaker, setSpeaker] = useState('');
  const [calendarDays, setCalendarDays] = useState(null);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  // השמעת קובץ לפני העלאה — ישירות מהמחשב (object URL), קובץ אחד בכל פעם
  const [listening, setListening] = useState(null);
  const listenUrlRef = useRef('');

  const stopListening = useCallback(() => {
    if (listenUrlRef.current) { URL.revokeObjectURL(listenUrlRef.current); listenUrlRef.current = ''; }
    setListening(null);
  }, []);
  const toggleListening = (r) => {
    const same = listening?.key === r.key;
    stopListening();
    if (same) return;
    const url = URL.createObjectURL(r.file);
    listenUrlRef.current = url;
    setListening({ key: r.key, url });
  };
  useEffect(() => () => { if (listenUrlRef.current) URL.revokeObjectURL(listenUrlRef.current); }, []);

  useEffect(() => {
    stopListening();
    if (!open) return;
    setRows([]);
    setSpeaker(defaultSpeaker);
    setCalendarDays(null);
    setEditKey(null);
  }, [open, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  /** שולח את כל השורות לבדיקה בשרת (זיהוי דף + תאריך בלוח + כפילויות) */
  const preview = async (list) => {
    setRows(list);
    if (!list.length) return;
    setChecking(true);
    try {
      const items = list.map((r) => ({ name: r.name, ...(r.manual || {}) }));
      const { data } = await api.post('/drashas/bulk/preview', { channel, items });
      setCalendarDays(data.calendarDays);
      setRows(list.map((r, i) => ({ ...r, plan: data.rows[i] })));
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בבדיקת הקבצים'));
    } finally {
      setChecking(false);
    }
  };

  const addFiles = (fileList) => {
    const files = [...(fileList || [])].filter((f) => f.type.startsWith('audio/') || AUDIO_FILE_RE.test(f.name));
    if (!files.length) { toast.error('לא נבחרו קבצי שמע'); return; }
    const known = new Set(rows.map((r) => r.key));
    const added = files
      .map((f) => ({ key: `${f.name}|${f.size}|${f.lastModified}`, file: f, name: f.name, manual: null, status: 'pending' }))
      .filter((r) => !known.has(r.key));
    preview([...rows, ...added]);
  };
  const removeRow = (key) => preview(rows.filter((r) => r.key !== key));
  const saveManual = (key, ref) => {
    setEditKey(null);
    preview(rows.map((r) => (r.key === key ? { ...r, manual: { ...ref, part: r.plan?.part || 1 } } : r)));
  };
  const patchRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const ready = rows.filter(rowReady);
  const errors = rows.filter((r) => r.plan?.error).length;
  const done = rows.filter((r) => r.status === 'done').length;

  const upload = async () => {
    // מהתאריך המוקדם למאוחר — כך השיעור המאוחר מבין שנפתחו כבר הוא הראשון בערוץ
    const queue = [...ready].sort((a, b) => (a.plan.date === b.plan.date ? a.name.localeCompare(b.name, 'he') : a.plan.date < b.plan.date ? -1 : 1));
    if (!queue.length) return;
    setRunning(true);
    let ok = 0;
    for (const r of queue) {
      patchRow(r.key, { status: 'uploading', uploadError: '' });
      const fd = new FormData();
      fd.append('audio', r.file);
      fd.append('channel', channel);
      fd.append('tractate', r.plan.tractate);
      fd.append('daf', String(r.plan.daf));
      fd.append('amud', r.plan.amud || '');
      fd.append('part', String(r.plan.part || 1));
      if (speaker.trim()) fd.append('speaker', speaker.trim());
      try {
        await api.post('/drashas/bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        patchRow(r.key, { status: 'done' });
        ok += 1;
      } catch (e) {
        patchRow(r.key, { status: 'failed', uploadError: apiError(e, 'ההעלאה נכשלה') });
      }
    }
    try {
      await api.post('/drashas/bulk/finish', { channel });
    } catch (e) {
      toast.error(apiError(e, 'השיעורים הועלו, אך עדכון רשימת הערוץ בטלפון נכשל'));
    }
    setRunning(false);
    if (ok === queue.length) toast.success(`${ok} שיעורים הועלו — כל אחד ייפתח בתאריך שלו`);
    else toast(`${ok} מתוך ${queue.length} הועלו. אפשר לנסות שוב את מה שנכשל.`, { tone: 'warning' });
    onDone?.();
  };

  const statusCell = (r) => {
    if (r.status === 'uploading') return <span className="nx-bulk-row__status"><Spinner size={14} />מעלה…</span>;
    if (r.status === 'done') return <Tag tone="success" icon="check">הועלה</Tag>;
    if (r.status === 'failed') return <Tag tone="danger" icon="alert" title={r.uploadError}>{r.uploadError || 'נכשל'}</Tag>;
    if (!r.plan) return checking ? <Spinner size={14} /> : null;
    if (r.plan.error) return <Tag tone="danger" icon="alert">{r.plan.error}</Tag>;
    return r.plan.scheduled
      ? <Tag tone="info" icon="clock">{`ייפתח ב־${r.plan.hebrewDate || dayMonth(r.plan.date)}`}</Tag>
      : <Tag tone="primary" icon="play">{r.plan.date < todayKey() ? 'עבר — ייכנס לסוף הרשימה' : 'היום — ייכנס מיד'}</Tag>;
  };

  return (
    <Dialog
      open={open}
      onClose={() => !running && onClose()}
      title={`העלאה מרוכזת — ${channelLabel}`}
      icon="upload"
      size="lg"
      footer={(
        <>
          <small className="ck-muted" style={{ marginInlineEnd: 'auto' }}>
            {rows.length ? `${rows.length} קבצים · ${ready.length} מוכנים${errors ? ` · ${errors} עם שגיאה` : ''}${done ? ` · ${done} הועלו` : ''}` : ''}
          </small>
          <Button variant="ghost" disabled={running} onClick={onClose}>{done ? 'סגירה' : 'ביטול'}</Button>
          <Button variant="primary" icon="upload" disabled={running || checking || !ready.length} onClick={upload}>
            {running ? 'מעלה…' : `העלאת ${ready.length} שיעורים`}
          </Button>
        </>
      )}
    >
      <div className="nx-stack">
        <Banner tone="info" icon="info">
          {'שם כל קובץ: מסכת, דף ועמוד — למשל '}
          <b>ברכות כג א.mp3</b>
          {' או '}
          <b>ברכות 23 ב.mp3</b>
          {'. בלי עמוד = כל הדף. חלק נוסף לאותו עמוד: '}
          <b>ברכות כג א (2).mp3</b>
          {'. כל שיעור ייפתח בטלפון ביום שבו הדף נלמד בלוח הלימודים, ועד אז הוא מוסתר.'}
        </Banner>
        {calendarDays === 0 ? <Banner tone="warning" icon="alert">אין דפים בלוח הלימודים למסלול הזה — ייבאו את התוכנית במסך «לוח לימודים».</Banner> : null}
        <Field label="מרצה (לכל הקבצים)">
          <TextInput value={speaker} disabled={running} onChange={(e) => setSpeaker(e.target.value)} />
        </Field>
        <div
          className={`ck-drop ${over ? 'is-over' : ''} ${running ? 'is-disabled' : ''}`.trim()}
          role="button"
          tabIndex={running ? -1 : 0}
          onClick={() => !running && inputRef.current?.click()}
          onKeyDown={(e) => { if (!running && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click(); } }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); if (!running) addFiles(e.dataTransfer.files); }}
        >
          <span className="ck-drop__icon"><Icon name="mic" size={26} /></span>
          <b className="ck-drop__title">גררו לכאן את כל קבצי השיעורים, או לחצו לבחירה</b>
          <small className="ck-drop__hint">אפשר לבחור הרבה קבצים בבת אחת · wav / mp3 · עד 50MB לקובץ</small>
          <input ref={inputRef} type="file" accept="audio/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>

        {rows.length ? (
          <div className="nx-bulk-list">
            {rows.map((r) => {
              const pageLabel = r.plan?.tractate ? formatLessonPageRefDisplay(r.plan, toHebrewNumeral) : '';
              return (
                <div key={r.key} className={`nx-bulk-row ${r.plan?.error ? 'is-error' : ''}`}>
                  <div className="nx-bulk-row__main">
                    <span className="nx-bulk-row__name" title={r.name}>{r.name}</span>
                    <span className="nx-bulk-row__meta">
                      {pageLabel ? <span><Icon name="book" size={13} />{`${pageLabel}${r.plan.part > 1 ? ` · חלק ${r.plan.part}` : ''}`}</span> : null}
                      {r.plan?.date ? <span><Icon name="calendar" size={13} />{`${r.plan.hebrewDate} (${dayMonth(r.plan.date)})`}</span> : null}
                      {r.plan?.existing ? <span className="is-warn"><Icon name="alert" size={13} />יש כבר שיעור לדף — החדש יושמע</span> : null}
                      {r.plan?.duplicateInBatch ? <span className="is-warn"><Icon name="copy" size={13} />אותו דף כמה פעמים בהעלאה</span> : null}
                    </span>
                    {listening?.key === r.key ? (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <audio className="nx-bulk-row__audio" src={listening.url} controls autoPlay onEnded={stopListening} />
                    ) : null}
                    {editKey === r.key ? <BulkPageEditor initial={r.plan} onSave={(ref) => saveManual(r.key, ref)} onCancel={() => setEditKey(null)} /> : null}
                  </div>
                  <div className="nx-bulk-row__side">
                    <IconButton size="sm" icon={listening?.key === r.key ? 'pause' : 'play'} label={listening?.key === r.key ? 'עצירה' : 'השמעה'} onClick={() => toggleListening(r)} />
                    {statusCell(r)}
                    {r.status !== 'done' && r.status !== 'uploading' && !running ? (
                      <>
                        <IconButton size="sm" icon="edit" label="שיוך ידני לדף" onClick={() => setEditKey(r.key)} />
                        <IconButton size="sm" icon="x" label="הסרה מהרשימה" onClick={() => { if (listening?.key === r.key) stopListening(); removeRow(r.key); }} />
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ── הוספת תוכן מדיאלוג אחד — בוחרים שלוחה, פרטים וקובץ שמע ─────── */
const PATH_GROUPS = { '77/1/1': 'דף היומי', '77/2/1': 'השיעור היומי (שינון)', '77/5': 'כל השיעורים', '77/6/1': 'דברות קודש', '77/6/2': 'מעמדים' };
const groupLabel = (path) => (PATH_GROUPS[path] ? `${PATH_GROUPS[path]} · ${path}` : `שלוחה ${path}`);

function AddContentDialog({ open, onClose, defaultTarget, onCreated }) {
  const toast = useCkToast();
  const [targets, setTargets] = useState([]);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(emptyDrashaForm());
  const [file, setFile] = useState(null);
  const [activate, setActivate] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyDrashaForm());
    setFile(null);
    setActivate(true);
    const base = IS_MISHTAMERET
      ? contentLessonChannels().map((c) => ({ id: c.id, label: c.label, ivrPath: c.ivrPath }))
      : [{ id: 'drasha', label: 'דרשות / שמועס׳ן', ivrPath: '5' }];
    setTargets(base);
    setTarget(base.find((t) => t.id === defaultTarget) || null);
    if (!IS_MISHTAMERET) return;
    api.get('/shinun-magidim', { params: { all: '1' } }).then(({ data }) => {
      const magidim = (data.data || []).filter((m) => m.status === 'active').map((m) => ({ id: m.channel, label: `מג״ש — ${m.name}`, ivrPath: `77/2/1/5 · מקש ${m.menuKey}` }));
      setTargets((prev) => {
        const i = prev.findIndex((t) => t.id === 'shinun_special');
        return i < 0 ? [...prev, ...magidim] : [...prev.slice(0, i + 1), ...magidim, ...prev.slice(i + 1)];
      });
      const m = magidim.find((x) => x.id === defaultTarget);
      if (m) setTarget(m);
    }).catch(() => {});
  }, [open, defaultTarget]);

  // ברירות מחדל של השמעה מהגדרות הערוץ שנבחר
  useEffect(() => {
    if (!open || !target) return;
    api.get(`/drashas/channel-settings/${encodeURIComponent(target.id)}`)
      .then(({ data }) => setForm((f) => ({ ...f, announceDetails: data.announceDetails !== false, announcePageRef: data.announcePageRef === true })))
      .catch(() => {});
  }, [open, target]);

  const save = async () => {
    if (!target) { toast.error('בחרו לאיזו שלוחה להכניס'); return; }
    const { error, payload } = buildDrashaPayload({ ...form, status: 'inactive' }, target.id);
    if (error) { toast.error(error); return; }
    setSaving(true);
    try {
      const { data: created } = await api.post('/drashas', payload);
      let msg = `נוסף ל«${target.label}»`;
      if (file) {
        const fd = new FormData();
        fd.append('audio', file);
        const { data: res } = await api.post(`/drashas/${created._id}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.yemotSynced) {
          if (activate) { await api.put(`/drashas/${created._id}`, { status: 'active' }); msg += ' — הועלה, סונכרן והופעל'; } else msg += ' — האודיו הועלה וסונכרן';
        } else {
          toast('הפריט נשמר, אך הסנכרון לימות נכשל — בדקו טוקן ימות. הפריט נשאר כבוי.', { tone: 'warning' });
          msg = '';
        }
      } else msg += ' — כבוי עד העלאת קובץ שמע';
      if (msg) toast.success(msg);
      onCreated?.(target.id);
      onClose();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהוספה'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      title="הוספת תוכן לשלוחה"
      icon="plus"
      size="md"
      footer={(
        <>
          <Button variant="ghost" disabled={saving} onClick={onClose}>ביטול</Button>
          <Button variant="primary" icon={file ? 'upload' : 'check'} disabled={saving || !target} onClick={save}>{saving ? (file ? 'מעלה…' : 'שומר…') : (file ? 'הוספה והעלאה' : 'הוספה')}</Button>
        </>
      )}
    >
      <div className="nx-stack">
        <Field label="לאיזו שלוחה להכניס? *">
          <ComboBox
            value={target}
            onChange={setTarget}
            options={targets}
            getLabel={(o) => o?.label || ''}
            getKey={(o) => o?.id}
            renderOption={(o) => <span className="nx-target-opt"><span>{o.label}</span><small>{groupLabel(o.ivrPath.split(' ')[0])}{o.ivrPath.includes('מקש') ? ` · ${o.ivrPath.split('· ')[1]}` : ''}</small></span>}
            placeholder="חיפוש שלוחה / ערוץ…"
          />
        </Field>
        {target ? <Banner tone="info" icon="phone">{`ייכנס לשלוחה ${target.ivrPath} — «${target.label}». יתווסף בסוף הרשימה; ניתן לשנות סדר במסך הערוץ.`}</Banner> : null}
        <DrashaFormFields form={form} setForm={setForm} editing={false} />
        <Field label="קובץ שמע" hint="אופציונלי — אפשר להעלות גם אחר כך מהערוץ">
          <FileDrop accept="audio/*" icon="mic" title={file ? file.name : 'גררו קובץ שמע או לחצו לבחירה'} hint={file ? `${(file.size / 1024 / 1024).toFixed(1)} MB · לחצו להחלפה` : 'wav / mp3'} onFile={(f) => setFile(f || null)} />
        </Field>
        {file ? <Switch label="להפעיל מיד אחרי סנכרון לימות" checked={activate} onChange={setActivate} /> : null}
      </div>
    </Dialog>
  );
}

export default function ContentNext() {
  const [tab, setTab] = useState(() => {
    try { return sessionStorage.getItem('bchinot-content-tab') || 'messages'; } catch { return 'messages'; }
  });
  useEffect(() => { try { sessionStorage.setItem('bchinot-content-tab', tab); } catch { /* ignore */ } }, [tab]);
  const [addOpen, setAddOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const openAdd = (target) => { setAddTarget(target || null); setAddOpen(true); };

  const channelView = (ch) => (
    <DrashasTab
      key={ch.id}
      channel={ch.id}
      title={ch.label}
      ivrPath={ch.ivrPath}
      subtitle="אם מוגדר דרשן לערוץ — השמיעה מתיקיית playfile."
      itemLabel="שיעור"
      refreshKey={refreshKey}
      onAdd={() => openAdd(ch.id)}
    />
  );

  // קבוצות ניווט: הודעות, הקלטה, ואז ערוצים לפי שלוחת הטלפון
  const groups = IS_MISHTAMERET ? [
    { label: 'הודעות', items: [
      { key: 'messages', label: 'הודעות', icon: 'bell', render: () => <MessagesTab /> },
      { key: 'system-messages', label: 'הודעות למערכת', icon: 'inbox', render: () => <SystemMessagesTab /> },
    ] },
    { label: 'הקלטה בטלפון', items: [{ key: 'recorders', label: 'דרשנים (הקלטה)', icon: 'mic', render: () => <LessonRecordersTab /> }] },
    ...Object.values(contentLessonChannels().reduce((acc, ch) => {
      const g = acc[ch.ivrPath] || (acc[ch.ivrPath] = { label: groupLabel(ch.ivrPath), items: [] });
      g.items.push({ key: ch.id, label: ch.label, icon: 'play', render: () => channelView(ch) });
      if (ch.id === 'shinun_special') g.items.push({ key: 'shinun_magash', label: 'מג״ש בחצר קדשינו', icon: 'users', render: () => <ShinunMagashTab refreshKey={refreshKey} onAdd={openAdd} /> });
      return acc;
    }, {})),
  ] : [
    { label: 'תוכן', items: [
      { key: 'messages', label: 'הודעות', icon: 'bell', render: () => <MessagesTab /> },
      { key: 'drasha', label: 'דרשות', icon: 'play', render: () => <DrashasTab refreshKey={refreshKey} onAdd={() => openAdd('drasha')} /> },
    ] },
  ];
  const all = groups.flatMap((g) => g.items);
  const current = all.find((t) => t.key === tab) || all[0];
  const isChannel = !['messages', 'system-messages', 'recorders', 'shinun_magash'].includes(current.key);

  return (
    <>
      <PageHeader
        title="תוכן"
        sub={current.label}
        primary={{ label: IS_MISHTAMERET ? 'הוספת שיעור' : 'הוספת דרשה', icon: 'plus', onClick: () => openAdd(isChannel ? current.key : null) }}
      />
      {all.length > 4 ? (
        <div className="nx-content-layout">
          <div className="nx-sidenav-select">
            <Select value={current.key} onChange={setTab} aria-label="סוג תוכן" options={groups.flatMap((g) => g.items.map((it) => ({ value: it.key, label: `${g.label} — ${it.label}` })))} />
          </div>
          <nav className="nx-sidenav" aria-label="סוגי תוכן">
            {groups.map((g) => (
              <div key={g.label} className="nx-sidenav__group">
                <div className="nx-sidenav__label">{g.label}</div>
                {g.items.map((it) => (
                  <button key={it.key} type="button" className={`nx-sidenav__item ${it.key === current.key ? 'is-on' : ''}`} aria-current={it.key === current.key ? 'page' : undefined} onClick={() => setTab(it.key)}>
                    <Icon name={it.icon} size={15} />
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div className="nx-content-main">{current.render()}</div>
        </div>
      ) : (
        <>
          <Tabs items={all.map((t) => ({ value: t.key, label: t.label }))} value={current.key} onChange={setTab} ariaLabel="סוגי תוכן" />
          {current.render()}
        </>
      )}
      <AddContentDialog open={addOpen} onClose={() => setAddOpen(false)} defaultTarget={addTarget} onCreated={() => setRefreshKey((k) => k + 1)} />
    </>
  );
}
